/**
 * Content Browsing Type Definitions
 *
 * TypeScript interfaces for content browsing in the Nour Al-Arabia platform.
 * These types are SHARED across Student, Teacher, and Admin portals.
 * They align with the backend ContentController API (/api/v1/content/*).
 */

// =============================================================================
// Book Types
// =============================================================================

/**
 * Book type enum matching backend BookType
 * - STUDENT: Student textbook
 * - WORKBOOK: Practice workbook
 * - COMBINED: Combined student book and workbook
 */
export type BookType = 'STUDENT' | 'WORKBOOK' | 'COMBINED';

// =============================================================================
// Level Constants
// =============================================================================

/**
 * Canonical Level 1 code string. Matches the backend `Level.code` value
 * (`backend/src/main/java/com/nourarabia/entity/Level.java:36`) and is
 * also surfaced on every {@link ContentBook} as `levelCode`.
 *
 * Phase 14 — Level 1 books are word-only. The 7 sentence-based games
 * (introduced in Phase 13/14) are filtered out of the create-game wizard
 * for L1 books, the AI sentence-extraction endpoint refuses L1 sources,
 * and the backend `/templates` create call rejects sentence-based types
 * for L1 books with HTTP 400 (i18n key `errors.gameTypeNotAllowedForLevel`).
 *
 * Use this constant everywhere the L1 check happens — never hard-code `'L1'`.
 */
export const LEVEL_CODE_L1 = 'L1' as const;

// =============================================================================
// Content DTOs
// =============================================================================

/**
 * Level DTO for content browsing
 *
 * Represents a curriculum level (e.g., L1-L6) with bilingual names
 * and a count of available books.
 */
export interface ContentLevel {
  /** Unique identifier (UUID) */
  id: string;
  /** Level code (e.g., 'L1', 'L2', 'L3') */
  code: string;
  /** Arabic name of the level */
  nameAr: string;
  /** English name of the level */
  nameEn: string;
  /** Display order for sorting (1-6) */
  displayOrder: number;
  /** Number of active books in this level */
  booksCount: number;
}

/**
 * Book DTO for content browsing
 *
 * Represents a book with its metadata for listing and navigation.
 */
export interface ContentBook {
  /** Unique identifier (UUID) */
  id: string;
  /** Parent level code (e.g., 'L1') */
  levelCode: string;
  /** Book type (STUDENT, WORKBOOK, COMBINED) */
  type: BookType;
  /** Arabic title of the book */
  titleAr: string;
  /** English title of the book */
  titleEn: string;
  /** Cover image URL (null if no cover uploaded) */
  coverImageUrl: string | null;
  /** Whether this book has a PDF file available */
  hasPdf: boolean;
  /** Number of audio files associated with this book */
  audioCount: number;
}

/**
 * Signed URL response for PDF viewing
 *
 * Contains a pre-signed URL to access a protected PDF file.
 * URLs expire after the specified duration for security.
 */
export interface PdfViewUrlResponse {
  /** Pre-signed URL for PDF access */
  url: string;
  /** URL expiration time in seconds (typically 900 = 15 minutes) */
  expiresInSeconds: number;
}

/**
 * Audio file DTO for playback
 *
 * Represents a single audio file associated with a book page.
 */
export interface ContentAudioFile {
  /** Unique identifier (UUID) */
  id: string;
  /** Page number this audio is associated with */
  pageNumber: number;
  /** Arabic title of the audio */
  titleAr: string;
  /** English title of the audio (optional) */
  titleEn: string | null;
  /** Pre-signed URL for audio streaming */
  url: string;
  /** Audio duration in seconds */
  durationSeconds: number;
  /** Display order within the book */
  displayOrder: number;
}

/**
 * Audio list response for a book
 *
 * Contains the book metadata along with all its audio files.
 */
export interface ContentAudioList {
  /** Book this audio list belongs to */
  book: ContentBook;
  /** List of audio files for the book */
  audioFiles: ContentAudioFile[];
}

/**
 * Video DTO for the book reader (YouTube per-page video)
 *
 * Represents a single YouTube video associated with a book page. The reader
 * fetches the book-level list and filters by page number client-side, exactly
 * like audio (the shared viewer only knows bookId + page number).
 */
export interface ContentVideo {
  /** Unique identifier (UUID) */
  id: string;
  /** Page number this video is associated with */
  pageNumber: number;
  /** Arabic title of the video */
  titleAr: string;
  /** English title of the video (optional) */
  titleEn: string | null;
  /** YouTube video ID (rendered via the embedded player) */
  youtubeVideoId: string;
  /** Display order within the book */
  displayOrder: number;
}

/**
 * Video list response for a book
 *
 * Contains the book metadata along with all its videos.
 */
export interface ContentVideoList {
  /** Book this video list belongs to */
  book: ContentBook;
  /** List of videos for the book */
  videos: ContentVideo[];
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get localized level name based on language preference
 *
 * @param level - The content level object
 * @param isArabic - True for Arabic, false for English
 * @returns The localized level name
 *
 * @example
 * const name = getLevelName(level, true); // Returns Arabic name
 */
export function getLevelName(level: ContentLevel, isArabic: boolean): string {
  return isArabic ? level.nameAr : level.nameEn;
}

/**
 * Get localized book title based on language preference
 *
 * @param book - The content book object
 * @param isArabic - True for Arabic, false for English
 * @returns The localized book title
 *
 * @example
 * const title = getBookTitle(book, false); // Returns English title
 */
export function getBookTitle(book: ContentBook, isArabic: boolean): string {
  return isArabic ? book.titleAr : book.titleEn;
}

/**
 * Get localized audio title based on language preference
 *
 * Falls back to Arabic title if English title is not available.
 *
 * @param audio - The content audio file object
 * @param isArabic - True for Arabic, false for English
 * @returns The localized audio title (falls back to Arabic if English is null)
 *
 * @example
 * const title = getAudioTitle(audio, false); // Returns English or Arabic fallback
 */
export function getAudioTitle(
  audio: ContentAudioFile,
  isArabic: boolean
): string {
  return isArabic ? audio.titleAr : audio.titleEn || audio.titleAr;
}

/**
 * Get localized video title based on language preference
 *
 * Falls back to Arabic title if English title is not available.
 *
 * @param video - The content video object
 * @param isArabic - True for Arabic, false for English
 * @returns The localized video title (falls back to Arabic if English is null)
 */
export function getVideoTitle(
  video: ContentVideo,
  isArabic: boolean
): string {
  return isArabic ? video.titleAr : video.titleEn || video.titleAr;
}

/**
 * Build the YouTube embed URL for an iframe player.
 *
 * Includes an `origin` param (the page's own origin) so YouTube can validate
 * the embedding domain even though the site sends a site-wide `no-referrer`
 * policy (to keep reset/invite tokens out of the Referer header) — without it
 * the player fails with "Video player configuration error (153)".
 *
 * @param youtubeVideoId - The YouTube video ID
 * @returns The embeddable player URL
 *
 * @example
 * getYouTubeEmbedUrl('dQw4w9WgXcQ'); // 'https://www.youtube.com/embed/dQw4w9WgXcQ?origin=...&rel=0&modestbranding=1&playsinline=1'
 */
export function getYouTubeEmbedUrl(youtubeVideoId: string): string {
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
  });
  if (typeof window !== 'undefined') {
    params.set('origin', window.location.origin);
  }
  return `https://www.youtube.com/embed/${youtubeVideoId}?${params.toString()}`;
}

/**
 * Build the YouTube thumbnail URL for a video ID.
 *
 * @param youtubeVideoId - The YouTube video ID
 * @returns The hqdefault thumbnail URL
 */
export function getYouTubeThumbnailUrl(youtubeVideoId: string): string {
  return `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`;
}

/**
 * Format duration from seconds to MM:SS format
 *
 * Handles edge cases like 0 seconds and values over an hour.
 *
 * @param seconds - Duration in seconds (must be non-negative)
 * @returns Formatted duration string in MM:SS format
 *
 * @example
 * formatDuration(90);   // Returns "1:30"
 * formatDuration(0);    // Returns "0:00"
 * formatDuration(3661); // Returns "61:01"
 */
export function formatDuration(seconds: number): string {
  // Ensure non-negative value
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(safeSeconds / 60);
  const secs = safeSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get localized book type label
 *
 * Translates the BookType enum to a human-readable label.
 *
 * @param type - The book type (STUDENT, WORKBOOK, COMBINED)
 * @param isArabic - True for Arabic, false for English
 * @returns The localized book type label
 *
 * @example
 * getBookTypeLabel('STUDENT', true);  // Returns 'كتاب الطالب'
 * getBookTypeLabel('WORKBOOK', false); // Returns 'Workbook'
 */
export function getBookTypeLabel(type: BookType, isArabic: boolean): string {
  const labels: Record<BookType, { ar: string; en: string }> = {
    STUDENT: { ar: 'كتاب الطالب', en: 'Student Book' },
    WORKBOOK: { ar: 'كراسة التدريبات', en: 'Workbook' },
    COMBINED: { ar: 'كتاب مدمج', en: 'Combined' },
  };
  return isArabic ? labels[type].ar : labels[type].en;
}

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Check if a book has available content (PDF or audio)
 *
 * @param book - The content book object
 * @returns True if the book has either PDF or audio content
 */
export function hasContent(book: ContentBook): boolean {
  return book.hasPdf || book.audioCount > 0;
}

/**
 * Check if a PDF URL response is still valid
 *
 * Provides a buffer of 60 seconds before actual expiration
 * to account for network latency.
 *
 * @param response - The PDF URL response
 * @param elapsedSeconds - Seconds elapsed since URL was generated
 * @returns True if the URL is still valid (with 60-second buffer)
 */
export function isPdfUrlValid(
  response: PdfViewUrlResponse,
  elapsedSeconds: number
): boolean {
  // Add 60-second buffer before expiration
  return elapsedSeconds < response.expiresInSeconds - 60;
}
